import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";
import { FieldArray, useFormik } from "formik";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import StatesValidationSchema from "../Validations/AcademicsValidation";
import Staets from "./States";
import DeleteIcon from "@mui/icons-material/Delete";
function RegulationModal({
  States,
  open,
  onClose,
  regulationsEdit,
  onSuccess,
}) {
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [universities, setUniversity] = useState([]);
  const [courses, SetCourses] = useState([]);

  // Fetch dropdown data once
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const universitiesRes = await api.get("/getAllUniversities");
        setUniversity(universitiesRes.data.data);
        const CoursesRes = await api.get("/getCourses", { params: { active: true } });
        SetCourses(CoursesRes.data.data);
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching dropdown data";
        setError(message);
        console.error("Dropdown fetch error:", err);
      }
    };

    fetchDropdownData();
  }, []);

  //  Form vlaidation using useFormik hook
  const formik = useFormik({
    initialValues: {
      regulationName: regulationsEdit?.regulationName || "",
      startYear: regulationsEdit?.startYear || "",
      endYear: regulationsEdit?.endYear || "",
      description: regulationsEdit?.description || "",
      order: regulationsEdit?.order || 0,
      courseIds: (() => {
        const raw = regulationsEdit?.courseIds ?? regulationsEdit?.courseId;
        if (!raw) return [];
        return Array.isArray(raw) ? raw : [raw];
      })(),
      universityId: regulationsEdit?.universityId || "",
      active: regulationsEdit ? !!regulationsEdit.active : true,

      customAttributes: (() => {
        const attrs =
          regulationsEdit?.customAttributes?.years ||
          regulationsEdit?.customAttributes;
        return Array.isArray(attrs) && attrs.length > 0
          ? attrs.map((item) => ({
              value: item.key || item.val || "", // single value
              description: item.description || "",
            }))
          : [{ value: "", description: "" }];
      })(),
    },

    validationSchema: StatesValidationSchema.ReulationvalidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      console.log("hello");
      try {
        setSubmitting(true);

        //  convert pair → key + val
        const cleanedAttributes = values.customAttributes
          .map((item) => {
            const val = item.value?.trim() || "";
            return {
              key: val,
              val: val,
              description: item.description || "",
            };
          })
          .filter((item) => item.key || item.description);

        const payload = {
          ...values,
          customAttributes: {
            years: cleanedAttributes,
          },
        };

        let response;

        if (regulationsEdit?.id) {
          response = await api.put(
            `/api/regulation-years/${regulationsEdit?.id}`,
            payload,
          );
        } else {
          response = await api.post("/api/regulation-years", payload);
        }

        toast.success(
          `Regulations ${
            regulationsEdit?.id ? "Updated" : "Added"
          } successfully!`,
          {
            position: "top-right",
            autoClose: 2000,
          },
        );

        onSuccess(response.data.data);
        resetForm();
        onClose();
      } catch (error) {
        toast.error("Failed to submit. Please try again.", {
          autoClose: 3000,
        });
        console.error("API Error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Filter courses based on selected university
  const filteredCourses = formik.values.universityId
    ? courses.filter(
        (course) => course.universityId === formik.values.universityId,
      )
    : courses;
  // Handle university change
  const handleUniversityChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("universityId", value);
    formik.setFieldValue("courseIds", []); // reset course
  };

  // add and deleting the addAttributes
  const addAttribute = () => {
    formik.setFieldValue("customAttributes", [
      ...formik.values.customAttributes,
      { value: "", description: "" },
    ]);
  };

  const removeAttribute = (index) => {
    const updated = [...formik.values.customAttributes];
    updated.splice(index, 1);
    formik.setFieldValue("customAttributes", updated);
  };

  // Format custom attributes for API submission
  // const formattedData = values.customAttributes.map((item) => {
  //   const [key, val] = (item.pair || "").split(":");
  //   return {
  //     key: key?.trim(),
  //     val: val?.trim(),
  //   };
  // });

  return (
    <>
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 } }}
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {regulationsEdit && Object.keys(regulationsEdit).length > 0
              ? "Update Regulations"
              : "Add Regulations"}
          </Typography>
          <Box sx={FORM_STYLES.container}>
            <TextField
              label="Regulation Name"
              name="regulationName"
              value={formik.values.regulationName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.regulationName &&
                Boolean(formik.errors.regulationName)
              }
              helperText={
                formik.touched.regulationName && formik.errors.regulationName
              }
              {...TEXT_FIELD_STYLES.standard}
            />
            <TextField
              label="Start Year"
              name="startYear"
              type="number"
              value={formik.values.startYear}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{
                min: 1900,
                max: 2100,
              }}
              error={
                formik.touched.startYear && Boolean(formik.errors.startYear)
              }
              helperText={formik.touched.startYear && formik.errors.startYear}
              {...TEXT_FIELD_STYLES.standard}
            />

            <TextField
              label="End Year"
              name="endYear"
              type="number"
              value={formik.values.endYear}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{
                min: formik.values.startYear || 1900,
                max: 2100,
              }}
              error={formik.touched.endYear && Boolean(formik.errors.endYear)}
              helperText={formik.touched.endYear && formik.errors.endYear}
              {...TEXT_FIELD_STYLES.standard}
            />

            <TextField
              multiline
              label="Description"
              name="description"
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              {...TEXT_FIELD_STYLES.standard}
            />
            <TextField
              label="Order"
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.order && Boolean(formik.errors.order)}
              helperText={formik.touched.order && formik.errors.order}
              {...TEXT_FIELD_STYLES.standard}
            />
            <FormControl
              fullWidth
              error={
                formik.touched.universityId &&
                Boolean(formik.errors.universityId)
              }
            >
              <InputLabel>University</InputLabel>
              <Select
                name="universityId"
                value={formik.values.universityId}
                onChange={handleUniversityChange}
                label="university"
                onBlur={formik.handleBlur}
                error={
                  formik.touched.universityId &&
                  Boolean(formik.errors.universityId)
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {universities.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.universityId && formik.errors.universityId && (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.universityId}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl
              fullWidth
              sx={{ marginTop: "20px" }}
              error={
                formik.touched.courseIds && Boolean(formik.errors.courseIds)
              }
            >
              <InputLabel>Courses</InputLabel>
              <Select
                multiple
                name="courseIds"
                value={formik.values.courseIds}
                onChange={(e) =>
                  formik.setFieldValue("courseIds", e.target.value)
                }
                onBlur={formik.handleBlur}
                input={<OutlinedInput label="Courses" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const course = courses.find((c) => c.id === id);
                      return (
                        <Chip
                          key={id}
                          label={course?.courseName || id}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
              >
                {filteredCourses.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.courseName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.courseIds && formik.errors.courseIds && (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.courseIds}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl
              fullWidth
              error={formik.touched.active && Boolean(formik.errors.active)}
              sx={{ mt: 2 }}
            >
              <InputLabel id="active-label">Active</InputLabel>
              <Select
                labelId="active-label"
                id="active"
                name="active"
                value={formik.values.active}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Active"
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {formik.touched.active && formik.errors.active && (
                <FormHelperText>{formik.errors.active}</FormHelperText>
              )}
            </FormControl>
            {formik.values.customAttributes.map((attr, index) => (
              <Box
                key={index}
                sx={{
                  mt: 2,
                  mb: 3,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  {/* <TextField
                    label="Key"
                    name={`customAttributes.${index}.key`}
                    value={attr.key || ""}
                    onChange={formik.handleChange}
                    fullWidth
                  />

                  <TextField
                    label="Value"
                    name={`customAttributes.${index}.val`}
                    value={attr.val || ""}
                    onChange={formik.handleChange}
                    fullWidth
                  /> */}
                  <TextField
                    label="Value"
                    name={`customAttributes.${index}.value`}
                    value={attr.value || ""}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Description"
                    name={`customAttributes.${index}.description`}
                    value={attr.description || ""}
                    onChange={formik.handleChange}
                    fullWidth
                    multiline
                    rows={2}
                  />

                  <Tooltip title="Remove">
                    <IconButton
                      onClick={() => removeAttribute(index)}
                      sx={{
                        height: 45,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: "error.main",
                          transform: "scale(1.1)",
                          backgroundColor: "rgba(255,0,0,0.08)",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}

            <Typography
              onClick={addAttribute}
              sx={{
                cursor: "pointer",
                color: "primary.main",
                fontWeight: 500,
                mt: 2,
                display: "inline-block",
              }}
            >
              + Add Attribute
            </Typography>

            <Box
              sx={FORM_STYLES.actions}
              style={{
                display: "flex",
                marginTop: "20px",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={formik.isSubmitting}
                {...BUTTON_STYLES.primary}
              >
                {formik.isSubmitting ? "Processing..." : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default RegulationModal;
